import { useEffect } from "react";

/**
 * Applies a community's chosen font to a resident-facing surface (iter335).
 *
 * Renders nothing visible. Loads the one Google stylesheet for that choice (once per URL, no
 * matter how many times this mounts) and scopes the faces to `[data-brandfont]` (or the
 * `scope` selector you pass), so the manager console keeps its own type while the portal, public
 * page and branded sign-in wear the community's. The font list is owned by the BACKEND
 * (`branding.py`) and arrives with the brand payload — there is no font table in the frontend to
 * drift out of sync.
 *
 * Usage: <BrandFont brand={me.community} /> and put `data-brandfont` on the wrapper.
 */

// iter353 — the stylesheet element id used to be the first 24 chars of btoa(url). Every Google
// Fonts URL shares the same 40-char prefix, so EVERY font hashed to the same id: only the first
// stylesheet on a page ever loaded and any other pick (the Community Profile preview, a second
// community in the same session) silently fell back to the system sans. Hash the whole URL.
const hash = (s) => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
};

export const loadFontStylesheet = (css) => {
  if (!css) return;
  const id = `brandfont-${hash(css)}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = css;
  document.head.appendChild(link);
};

export const BrandFont = ({ brand, scope = "[data-brandfont]" }) => {
  const css = brand?.font_css;
  const heading = brand?.font_heading;
  const body = brand?.font_body;

  useEffect(() => { loadFontStylesheet(css); }, [css]);

  if (!heading && !body) return null;
  return (
    <style>{`
      ${scope} { font-family: ${body}; }
      ${scope} .font-body { font-family: ${body}; }
      ${scope} h1, ${scope} h2, ${scope} h3,
      ${scope} h4, ${scope} .font-heading { font-family: ${heading}; }
    `}</style>
  );
};

export default BrandFont;
